import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Chip,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  makeStyles,
  createStyles,
  AppBar
} from '@material-ui/core';
import { CollapsedText } from '../../components/collapsed-text/collapsed-text';
import { TrainerData } from '.';
import CheckCircle from '@material-ui/icons/CheckCircle';
import { trackCustomEvent } from 'gatsby-plugin-google-analytics';
import {IntroVideo} from './intro-video'
import { useStaticQuery, graphql } from 'gatsby';
import { nameRepresentation } from '../../utils/text-formatting';
import { ScrollVisibleWrapper } from '../../components/scroll-pass-wrapper/scroll-pass.wrapper';
import theme from '../../gatsby-theme-material-ui-top-layout/theme';

interface Props {
  trainerData: TrainerData;
  location: Location;
}

const useStyles = makeStyles(({palette, breakpoints})=>createStyles({
  appBar: {
    backgroundColor: palette.background.paper,
    borderBottom: `solid 1px ${palette.divider}`,
    '&.MuiAppBar-positionSticky': {
      top: 66,
      [breakpoints.down('sm')]: {
        top: 58
      }
    }
  },
  root: {
    '& #stamped-main-widget': {
      margin: 0
    }
  }
}))

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
  onChange: (index:number)=> void;
}

function ScrollTabPanel(props: TabPanelProps) {
  const { children, value, index, onChange, ...other } = props;

  const handleChange = (isVisible: boolean) => {
    if(isVisible){
      onChange && onChange(index)
    }
  }

  return (
    <ScrollVisibleWrapper
      onChange={handleChange}
      role="tabpanel"
      id={`scroll-tabpanel-${index}`}
      aria-labelledby={`scroll-tabpanel-${index}`}
      offset={20}
      style={{scrollMarginTop: theme.spacing(6)}}
      {...other}
    >
      {children}
    </ScrollVisibleWrapper>
  );
}

export const TrainerDetails: React.FC<Props> = ({ trainerData, location }) => {
  const classes = useStyles()
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const name = nameRepresentation(trainerData.first_name, trainerData.last_name)
  const [value, setValue] = useState(0)
  const handleExpand = () => {
    trackCustomEvent({
      category: "Trainer Details",
      action: "Click",
      label: `more`,
      value: trainerData.price
    })
  }

  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  const canonicalUrl = data.site.siteMetadata.siteURL + location.pathname

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    // setValue(newValue)
    document.getElementById(`scroll-tabpanel-${newValue}`)?.scrollIntoView({ behavior: 'smooth' })
  }

  const params = new URLSearchParams(location.search)

  useLayoutEffect(()=>{
    // TODO: pass env variable
    window && window.StampedFn && window.StampedFn.init({ apiKey: "pubkey-pmoh8q00W0C69E998AU2Ri6TU39ZcF", storeUrl: "habfit.io" }); 
    // Replaces the text if there is no review
    if(document){
      setTimeout(()=> {
        const xpath = "//div[text()='Be the first to review this item']";
        const element = document.evaluate(xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
        if(element){
          element.textContent = "Be the first to review this trainer"
        }
      }, 3000)
    }
  }, [])

  const handleScrollPanelChange = (index: number) => {
    setValue(index)
  }

  const trainerNumber = process.env.GATSBY_IS_PRODUCTION && trainerData.trainer_number || 123456789
  
  

  const mainDetails = (
      <ScrollTabPanel value={value} index={0} onChange={handleScrollPanelChange}>
        {
          trainerData.intro_video && (
            <IntroVideo thumbnail={trainerData.intro_video_thumbnail} videoSrc={trainerData.intro_video} />
          )
        }
        <Typography variant="subtitle1">{trainerData.bio}</Typography>
        <Box mt={2} />
        <Typography component="h2" variant="h6">Summary</Typography>
        <Box mt={1} />
        <CollapsedText onExpand={handleExpand} maxNumberOfWords={isDesktop ? 16 : 14}>
          {trainerData.summary}
        </CollapsedText>
        <Box mt={2} />
        <Typography component="h2" variant="h6">Languages</Typography>
        <Box mt={1} />
        <Grid container>
          {trainerData.languages &&
            trainerData.languages.map(lang => (
              <Box key={lang} mt={1} mr={1}>
                <Chip label={lang} size="medium" />
              </Box>
            ))}
        </Grid>
        <Box mt={2} display="flex">
          <Typography component="h2" variant="h6">Certifications</Typography>
          <CheckCircle color="primary" fontSize="small" />
        </Box>
        <Box mt={1} />
        <Grid container>
          {trainerData.certification &&
            trainerData.certification.map(cert => (
              <Box key={cert.cert_name} mt={1} mr={1}>
                <Chip label={cert.cert_name} size="medium" />
              </Box>
            ))}
        </Grid>
      </ScrollTabPanel>
  )
  

    return (
      
      <Box className={classes.root}>
        <AppBar elevation={0} className={classes.appBar} position="sticky" color="default">
          <Tabs
            value={value}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            aria-label="scalable auto tabs example"
          >
            <Tab label="About" />
            <Tab label="Reviews" />
          </Tabs>
        </AppBar>
        <Box mt={2}/>
        {mainDetails}
        <ScrollTabPanel value={value} index={1} onChange={handleScrollPanelChange} >
          <Box mt={2} />
          <Typography component="h2" variant="h6">Reviews</Typography>
          <div id="stamped-main-widget" 
            data-product-id={trainerNumber}
            data-name={name}
            data-url={canonicalUrl}
            data-image-url={trainerData.profile_pic_url}
            data-description={trainerData.summary}
            > 
          </div>
        </ScrollTabPanel>
      </Box>
    )
};